import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Sidebar from "../components/Sidebar";
import AvatarModal from "../components/AvatarModal";
import { useAvatarUpload } from "../hooks/useAvatarUpload";
import { useUser } from "../context/UserContext";
import { supabase } from "../lib/supabase";
import PostViewerModal from "../components/PostViewerModal";
import AboutAccountModal from "../components/AboutAccountModal";
import FollowListOverlay from "../components/FollowListOverlay";
import { faUserCircle, faBookmark, faImage } from "@fortawesome/free-regular-svg-icons";
import { faGear, faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons";
import "../styles/profile.css";

function Profile() {
    const {
        user, profile, setProfile, loading,
        cachedPosts, setCachedPosts, postsFetched, setPostsFetched
    } = useUser();

    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [showAccountInfo, setShowAccountInfo] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showFullBio, setShowFullBio] = useState(false);
    const [postsLoading, setPostsLoading] = useState(true);
    const [selectedPost, setSelectedPost] = useState(null);

    // Tab: "posts" | "saved"  (saved only shown on own profile)
    const [activeTab, setActiveTab] = useState("posts");

    // Bookmarked posts
    const [savedPosts, setSavedPosts] = useState([]);
    const [savedLoading, setSavedLoading] = useState(false);
    const [savedFetched, setSavedFetched] = useState(false);

    // Visiting user
    const { username } = useParams();
    const isVisitingUser = !!username;
    const [viewProfile, setViewProfile] = useState(null);
    const [viewPosts, setViewPosts] = useState([]);
    const [viewPostsLoading, setViewPostsLoading] = useState(false);
    const [viewPostsCount, setViewPostsCount] = useState(0);
    const [isFollowing, setIsFollowing] = useState(false);
    const [isFollowedBy, setIsFollowedBy] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);
    const [showUnfollowModal, setShowUnfollowModal] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);

    // Visitor dots
    const [showVisitorDotsMenu, setShowVisitorDotsMenu] = useState(false);
    const [copiedToast, setCopiedToast] = useState(false);

    // Follow list overlay
    const [followListMode, setFollowListMode] = useState(null);

    const currentProfile = isVisitingUser ? viewProfile : profile;
    const currentPosts = isVisitingUser ? viewPosts : cachedPosts;
    const finalAvatarUrl = isVisitingUser ? currentProfile?.avatar_url : avatarUrl;
    const canViewPrivatePosts = !isVisitingUser || !currentProfile?.is_private || (isFollowing && isFollowedBy);
    const canViewFollowList = !isVisitingUser || !currentProfile?.is_private || isFollowedBy;

    const navigate = useNavigate();

    const { uploadAvatar, removeAvatar } = useAvatarUpload(
        profile, setAvatarUrl, setAvatarLoading, setProfile
    );

    const isBioLong = currentProfile?.bio && (
        currentProfile.bio.length > 125 ||
        currentProfile.bio.split("\n").length > 3
    );

    useEffect(() => {
    document.title = "Profile • CodeGram";
  }, []);

    // ── URL SYNC for tabs ──────────────────────────────────────────────────────
    useEffect(() => {
        if (isVisitingUser) return;
        if (activeTab === "saved") {
            window.history.replaceState(null, "", "/profile/saved");
        } else {
            window.history.replaceState(null, "", "/profile");
        }
    }, [activeTab, isVisitingUser]);

    // Read tab from URL on mount
    useEffect(() => {
        if (isVisitingUser) return;
        if (window.location.pathname === "/profile/saved") {
            setActiveTab("saved");
        }
    }, [isVisitingUser]);

    // ── SELF REDIRECT ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isVisitingUser || !user || !profile) return;
        if (decodeURIComponent(username) === profile.username) {
            navigate("/profile", { replace: true });
        }
    }, [isVisitingUser, user, profile, username, navigate]);

    // ── FETCH OWN POSTS ───────────────────────────────────────────────────────
    const fetchPosts = useCallback(async () => {
        if (!user) return;
        setPostsLoading(true);
        const { data, error } = await supabase
            .from("posts")
            .select(`id, user_id, caption, created_at,
                post_images(id, image_path, order_index),
                likes(user_id)`)
            .eq("user_id", user.id)
            .order("created_at", { ascending: false });

        if (error) { setCachedPosts([]); setPostsFetched(true); setPostsLoading(false); return; }

        const formatted = await Promise.all(data.map(async (post) => {
            const sorted = (post.post_images || []).sort((a, b) => a.order_index - b.order_index);
            const withUrls = await Promise.all(sorted.map(async (img) => {
                const { data: sd } = await supabase.storage.from("posts").createSignedUrl(img.image_path, 3600);
                return { ...img, signedUrl: sd?.signedUrl || null };
            }));
            return { ...post, post_images: withUrls, likesCount: post.likes?.length || 0, isLiked: post.likes?.some(l => l.user_id === user.id) || false };
        }));

        setCachedPosts(formatted);
        setPostsFetched(true);
        setPostsLoading(false);
    }, [user, setCachedPosts, setPostsFetched]);

    // ── FETCH SAVED POSTS ─────────────────────────────────────────────────────
    const fetchSavedPosts = useCallback(async () => {
        if (!user) return;
        setSavedLoading(true);

        const { data: bookmarkData, error: bErr } = await supabase
            .from("bookmarks")
            .select("post_id, created_at")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false });

        if (bErr || !bookmarkData?.length) {
            setSavedPosts([]);
            setSavedLoading(false);
            setSavedFetched(true);
            return;
        }

        const postIds = bookmarkData.map(b => b.post_id);
        const { data: postsData } = await supabase
            .from("posts")
            .select(`id, user_id, caption, created_at,
                profiles:user_id(id, username, avatar_url),
                post_images(id, image_path, order_index),
                likes(user_id)`)
            .in("id", postIds);

        const formatted = await Promise.all((postsData || []).map(async (post) => {
            const sorted = (post.post_images || []).sort((a, b) => a.order_index - b.order_index);
            const withUrls = await Promise.all(sorted.map(async (img) => {
                const { data: sd } = await supabase.storage.from("posts").createSignedUrl(img.image_path, 3600);
                return { ...img, signedUrl: sd?.signedUrl || null };
            }));
            return { ...post, post_images: withUrls, likesCount: post.likes?.length || 0, isLiked: post.likes?.some(l => l.user_id === user.id) || false };
        }));

        // Preserve bookmark order
        const orderMap = {};
        bookmarkData.forEach((b, i) => { orderMap[b.post_id] = i; });
        formatted.sort((a, b) => (orderMap[a.id] ?? 999) - (orderMap[b.id] ?? 999));

        setSavedPosts(formatted);
        setSavedLoading(false);
        setSavedFetched(true);
    }, [user]);

    // Load saved when tab switches
    useEffect(() => {
        if (activeTab === "saved" && !isVisitingUser && !savedFetched) {
            fetchSavedPosts();
        }
    }, [activeTab, isVisitingUser, savedFetched, fetchSavedPosts]);

    // ── COPY LINK (generates /p/:postId) ──────────────────────────────────────
    const handleCopyProfileLink = () => {
        const url = `${window.location.origin}/user/${encodeURIComponent(currentProfile?.username)}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedToast(true);
            setTimeout(() => setCopiedToast(false), 2500);
        });
        setShowVisitorDotsMenu(false);
    };

    // When clicking a post in the grid, update URL to /p/:postId
    const openPost = (post, postList) => {
        setSelectedPost(post);
        window.history.replaceState(null, "", `/p/${post.id}`);
    };

    // When closing modal, restore profile URL
    const closePost = () => {
        setSelectedPost(null);
        const base = isVisitingUser
            ? `/user/${encodeURIComponent(currentProfile?.username)}`
            : activeTab === "saved" ? "/profile/saved" : "/profile";
        window.history.replaceState(null, "", base);
    };

    // ── FOLLOW ACTIONS ────────────────────────────────────────────────────────
    const handleFollow = async () => {
        if (!user || !viewProfile) return;
        setFollowLoading(true);
        const { error } = await supabase.from("follows").insert([{ follower_id: user.id, following_id: viewProfile.id }]);
        if (!error) setIsFollowing(true);
        setFollowLoading(false);
    };

    const handleUnfollow = async () => {
        if (!user || !viewProfile) return;
        setFollowLoading(true);
        const { error } = await supabase.from("follows").delete().eq("follower_id", user.id).eq("following_id", viewProfile.id);
        if (!error) { setIsFollowing(false); setShowUnfollowModal(false); }
        setFollowLoading(false);
    };

    // ── EFFECTS ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isVisitingUser || !user || !viewProfile) return;
        const check = async () => {
            const { data: f } = await supabase.from("follows").select("id").eq("follower_id", user.id).eq("following_id", viewProfile.id).maybeSingle();
            setIsFollowing(!!f);
            const { data: fb } = await supabase.from("follows").select("id").eq("follower_id", viewProfile.id).eq("following_id", user.id).maybeSingle();
            setIsFollowedBy(!!fb);
        };
        check();
    }, [isVisitingUser, user, viewProfile]);

    useEffect(() => {
        if (!loading && !user && !isVisitingUser) navigate("/");
    }, [loading, user, navigate, isVisitingUser]);

    useEffect(() => { setSelectedPost(null); }, [username]);

    useEffect(() => {
        if (!currentProfile) return;
        const fetch = async () => {
            const { count: f } = await supabase.from("follows").select("id", { count: "exact", head: true }).eq("following_id", currentProfile.id);
            const { count: fg } = await supabase.from("follows").select("id", { count: "exact", head: true }).eq("follower_id", currentProfile.id);
            setFollowersCount(f || 0);
            setFollowingCount(fg || 0);
        };
        fetch();
    }, [currentProfile]);

    useEffect(() => {
        if (!isVisitingUser) return;
        const fetchVP = async () => {
            const { data, error } = await supabase.from("profiles").select("*").eq("username", decodeURIComponent(username)).single();
            if (error) { setViewProfile(null); return; }
            setViewProfile(data);
        };
        fetchVP();
    }, [username, isVisitingUser]);

    useEffect(() => {
        if (!isVisitingUser || !viewProfile) return;
        const fetchCount = async () => {
            const { count } = await supabase.from("posts").select("id", { count: "exact", head: true }).eq("user_id", viewProfile.id);
            setViewPostsCount(count || 0);
        };
        fetchCount();

        if (viewProfile.is_private && !(isFollowing && isFollowedBy)) { setViewPosts([]); setViewPostsLoading(false); return; }

        const fetchVPosts = async () => {
            setViewPostsLoading(true);
            const { data, error } = await supabase.from("posts")
                .select(`id, user_id, caption, created_at,
                    post_images(id, image_path, order_index),
                    likes(user_id)`)
                .eq("user_id", viewProfile.id).order("created_at", { ascending: false });

            if (error) { setViewPosts([]); setViewPostsLoading(false); return; }

            const formatted = await Promise.all(data.map(async (post) => {
                const sorted = (post.post_images || []).sort((a, b) => a.order_index - b.order_index);
                const withUrls = await Promise.all(sorted.map(async (img) => {
                    const { data: sd } = await supabase.storage.from("posts").createSignedUrl(img.image_path, 3600);
                    return { ...img, signedUrl: sd?.signedUrl || null };
                }));
                return { ...post, post_images: withUrls, likesCount: post.likes?.length || 0, isLiked: post.likes?.some(l => l.user_id === user?.id) || false };
            }));
            setViewPosts(formatted);
            setViewPostsLoading(false);
        };
        fetchVPosts();
    }, [viewProfile, isVisitingUser, user, isFollowing, isFollowedBy]);

    useEffect(() => {
        if (!isVisitingUser) setAvatarUrl(profile?.avatar_url || null);
    }, [profile, isVisitingUser]);

    useEffect(() => {
        if (isVisitingUser || !user || postsFetched) return;
        fetchPosts();
    }, [user, postsFetched, fetchPosts, isVisitingUser]);

    useEffect(() => {
        if (!user || cachedPosts.length === 0) return;
        const refresh = async () => {
            const r = await Promise.all(cachedPosts.map(async (post) => {
                const imgs = await Promise.all(post.post_images.map(async (img) => {
                    const { data } = await supabase.storage.from("posts").createSignedUrl(img.image_path, 3600);
                    return { ...img, signedUrl: data?.signedUrl || null };
                }));
                return { ...post, post_images: imgs };
            }));
            setCachedPosts(r);
        };
        const interval = setInterval(refresh, 55 * 60 * 1000);
        return () => clearInterval(interval);
    }, [user, cachedPosts, setCachedPosts]);

    if (loading) return null;
    if (!currentProfile) return null;

    // Which post list is active for the modal
    const activePostList = activeTab === "saved" ? savedPosts : currentPosts;

    // PostGrid renderer (reused for both tabs)
    const renderGrid = (posts, isLoading) => (
        <div className="posts-grid">
            {isLoading && Array.from({ length: 9 }).map((_, i) => <div key={i} className="post-skeleton" />)}
            {!isLoading && posts.map((post) => {
                const firstImage = post.post_images?.[0];
                if (!firstImage?.signedUrl) return null;
                return (
                    <div key={post.id} className="post-box" onClick={() => openPost(post, posts)}>
                        <img src={firstImage.signedUrl} alt="post" className="post-img" />
                        {post.post_images.length > 1 && (
                            <div className="multi-icon">
                                <svg aria-label="Carousel" fill="white" height="20" viewBox="0 0 48 48" width="20">
                                    <path d="M40 10H20a6 6 0 0 0-6 6v20a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16a6 6 0 0 0-6-6Zm2 26a2 2 0 0 1-2 2H20a2 2 0 0 1-2-2V16a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2Z" />
                                    <path d="M28 6H12a6 6 0 0 0-6 6v16a2 2 0 0 0 4 0V12a2 2 0 0 1 2-2h16a2 2 0 0 0 0-4Z" />
                                </svg>
                            </div>
                        )}
                    </div>
                );
            })}
            {!isLoading && posts.length === 0 && (
                <div className="profile-empty-tab">
                    <FontAwesomeIcon icon={activeTab === "saved" ? faBookmark : faImage} className="profile-empty-icon" />
                    <p>{activeTab === "saved" ? "No saved posts yet." : "No posts yet."}</p>
                </div>
            )}
        </div>
    );

    return (
        <div className="profile-page">
            <Sidebar />

            <main className="profile-content">

                {/* HEADER */}
                <div className="profile-header">
                    <div
                        className={`profile-avatar clickable ${avatarLoading ? "loading" : ""}`}
                        onClick={() => { if (!isVisitingUser && !avatarLoading) setShowPhotoModal(true); }}
                    >
                        {finalAvatarUrl ? (
                            <img src={finalAvatarUrl} alt="Avatar" className="avatar-img" />
                        ) : (
                            <FontAwesomeIcon icon={faUserCircle} />
                        )}
                        {!isVisitingUser && avatarLoading && (
                            <div className="avatar-spinner"><div className="spinner" /></div>
                        )}
                    </div>

                    <div className="profile-info">
                        <div className="profile-top-row">
                            <h2 className="profile-username clickable" onClick={() => setShowAccountInfo(true)}>
                                {currentProfile.username}
                            </h2>

                            {!isVisitingUser ? (
                                <>
                                    <button className="profile-btn" onClick={() => navigate("/settings")}>Edit Profile</button>
                                    {/* <button className="profile-btn secondary">View Archive</button> */}
                                    <button className="icon-btn" onClick={() => setShowSettingsModal(true)}>
                                        <FontAwesomeIcon icon={faGear} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    {isFollowing ? (
                                        <button className="profile-btn secondary" onClick={() => setShowUnfollowModal(true)} disabled={followLoading}>
                                            {followLoading ? "..." : "Following"}
                                        </button>
                                    ) : (
                                        <button className="follow-btn" onClick={handleFollow} disabled={followLoading}>
                                            {followLoading ? "..." : (isFollowedBy ? "Follow Back" : "Follow")}
                                        </button>
                                    )}
                                    <button className="icon-btn visitor-dots-btn" onClick={() => setShowVisitorDotsMenu(true)}>•••</button>
                                </>
                            )}
                        </div>

                        <div className="profile-stats">
                            <span><strong>{isVisitingUser ? viewPostsCount : currentPosts.length}</strong> posts</span>
                            <span className={canViewFollowList ? "stats-clickable" : ""} onClick={() => canViewFollowList && setFollowListMode("followers")}>
                                <strong>{followersCount}</strong> followers
                            </span>
                            <span className={canViewFollowList ? "stats-clickable" : ""} onClick={() => canViewFollowList && setFollowListMode("following")}>
                                <strong>{followingCount}</strong> following
                            </span>
                        </div>

                        <div className="profile-name">{currentProfile.full_name}</div>

                        {currentProfile.bio && (
                            <div className="profile-bio">
                                <p className={`bio-text ${!showFullBio ? "clamped" : ""}`}>{currentProfile.bio}</p>
                                {isBioLong && (
                                    <span className="bio-more" onClick={() => setShowFullBio(!showFullBio)}>
                                        {showFullBio ? "less" : "...more"}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* TABS — only own profile shows Saved tab */}
                {!isVisitingUser && (
                    <div className="profile-tabs">
                        <button
                            className={`profile-tab ${activeTab === "posts" ? "active" : ""}`}
                            onClick={() => setActiveTab("posts")}
                        >
                            <FontAwesomeIcon icon={faImage} />
                            <span>Posts</span>
                        </button>
                        <button
                            className={`profile-tab ${activeTab === "saved" ? "active" : ""}`}
                            onClick={() => setActiveTab("saved")}
                        >
                            <FontAwesomeIcon icon={faBookmark} />
                            <span>Saved</span>
                        </button>
                    </div>
                )}

                {/* DIVIDER under tabs */}
                <div className="profile-tabs-divider" />

                {/* POSTS SECTION */}
                <div className="profile-posts-section">
                    {isVisitingUser && currentProfile.is_private && !canViewPrivatePosts ? (
                        <div className="private-account-box">
                            <div className="private-lock-circle">
                                <svg aria-label="Private" fill="white" height="28" viewBox="0 0 48 48" width="28">
                                    <path d="M24 4c-6.6 0-12 5.4-12 12v6h-2c-2.2 0-4 1.8-4 4v14c0 2.2 1.8 4 4 4h28c2.2 0 4-1.8 4-4V26c0-2.2-1.8-4-4-4h-2v-6c0-6.6-5.4-12-12-12Zm-8 12c0-4.4 3.6-8 8-8s8 3.6 8 8v6H16v-6Zm22 24H10V26h28v14Z" />
                                </svg>
                            </div>
                            <h3>This account is private</h3>
                            <p>Follow to see their photos and videos.</p>
                            {isFollowing ? (
                                <button className="profile-btn secondary" onClick={() => setShowUnfollowModal(true)} disabled={followLoading}>
                                    {followLoading ? "..." : "Following"}
                                </button>
                            ) : (
                                <button className="follow-btn" onClick={handleFollow} disabled={followLoading}>
                                    {followLoading ? "..." : (isFollowedBy ? "Follow Back" : "Follow")}
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* OWN PROFILE TABS */}
                            {!isVisitingUser && activeTab === "posts" && renderGrid(
                                cachedPosts,
                                !postsFetched && postsLoading
                            )}
                            {!isVisitingUser && activeTab === "saved" && renderGrid(
                                savedPosts,
                                savedLoading
                            )}
                            {/* VISITING USER */}
                            {isVisitingUser && renderGrid(viewPosts, viewPostsLoading)}
                        </>
                    )}
                </div>

                {/* ── MODALS ── */}

                {!isVisitingUser && (
                    <AvatarModal
                        open={showPhotoModal}
                        onClose={() => setShowPhotoModal(false)}
                        onUpload={uploadAvatar}
                        onRemove={removeAvatar}
                        hasAvatar={!!avatarUrl}
                    />
                )}

                {canViewPrivatePosts && selectedPost && (
                    <PostViewerModal
                        open={!!selectedPost}
                        onClose={closePost}
                        post={selectedPost}
                        posts={activePostList}
                        profile={activeTab === "saved" ? selectedPost.profiles || currentProfile : currentProfile}
                        onNextPost={() => {
                            const list = activePostList;
                            const i = list.findIndex(p => p.id === selectedPost.id);
                            if (i < list.length - 1) {
                                const next = list[i + 1];
                                setSelectedPost(next);
                                window.history.replaceState(null, "", `/p/${next.id}`);
                            }
                        }}
                        onPrevPost={() => {
                            const list = activePostList;
                            const i = list.findIndex(p => p.id === selectedPost.id);
                            if (i > 0) {
                                const prev = list[i - 1];
                                setSelectedPost(prev);
                                window.history.replaceState(null, "", `/p/${prev.id}`);
                            }
                        }}
                        onPostDeleted={(postId) => {
                            if (isVisitingUser) setViewPosts(prev => prev.filter(p => p.id !== postId));
                            else if (activeTab === "saved") setSavedPosts(prev => prev.filter(p => p.id !== postId));
                            else setCachedPosts(prev => prev.filter(p => p.id !== postId));
                            closePost();
                        }}
                        onPostUpdated={(postId, updatedData) => {
                            const updater = prev => prev.map(p => p.id === postId ? { ...p, ...updatedData } : p);
                            if (isVisitingUser) setViewPosts(updater);
                            else if (activeTab === "saved") setSavedPosts(updater);
                            else setCachedPosts(updater);
                            setSelectedPost(prev => prev?.id === postId ? { ...prev, ...updatedData } : prev);
                        }}
                    />
                )}

                <AboutAccountModal
                    open={showAccountInfo}
                    onClose={() => setShowAccountInfo(false)}
                    profile={currentProfile}
                    avatarUrl={finalAvatarUrl}
                />

                {!isVisitingUser && showSettingsModal && (
                    <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
                        <div className="photo-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-item action" onClick={() => { setShowSettingsModal(false); navigate("/settings"); }}>Settings and Privacy</div>
                            <div className="modal-divider" />
                            <div className="modal-item action remove" onClick={async () => { await supabase.auth.signOut(); navigate("/"); }}>Log out</div>
                            <div className="modal-divider" />
                            <div className="modal-item cancel" onClick={() => setShowSettingsModal(false)}>Cancel</div>
                        </div>
                    </div>
                )}

                {showUnfollowModal && (
                    <div className="modal-overlay" onClick={() => setShowUnfollowModal(false)}>
                        <div className="photo-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-item action remove" onClick={async () => { await handleUnfollow(); setShowUnfollowModal(false); }}>Unfollow</div>
                            <div className="modal-divider" />
                            <div className="modal-item cancel" onClick={() => setShowUnfollowModal(false)}>Cancel</div>
                        </div>
                    </div>
                )}

                {isVisitingUser && showVisitorDotsMenu && (
                    <div className="modal-overlay" onClick={() => setShowVisitorDotsMenu(false)}>
                        <div className="photo-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-item action" onClick={handleCopyProfileLink}>Copy link</div>
                            <div className="modal-divider" />
                            <div className="modal-item action" onClick={() => { setShowVisitorDotsMenu(false); setShowAccountInfo(true); }}>About this account</div>
                            <div className="modal-divider" />
                            <div className="modal-item cancel" onClick={() => setShowVisitorDotsMenu(false)}>Cancel</div>
                        </div>
                    </div>
                )}

                {copiedToast && <div className="profile-copy-toast">Link copied!</div>}

                <FollowListOverlay
                    open={!!followListMode}
                    onClose={() => setFollowListMode(null)}
                    profileId={currentProfile?.id}
                    mode={followListMode || "followers"}
                />

            </main>
        </div>
    );
}

export default Profile;
